import React, { useEffect, useRef } from 'react';
import OtpDigitInputs, { OTP_LENGTH } from './OtpDigitInputs';

const CheckBadge = () => (
  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white flex-shrink-0">
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

const StepPill = ({ n, label, active, done }) => (
  <div className={`flex items-center gap-2 min-w-0 ${done ? 'text-emerald-600 dark:text-emerald-400' : active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
    {done ? (
      <CheckBadge />
    ) : (
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
        active ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
      }`}
      >
        {n}
      </span>
    )}
    <span className="text-sm font-semibold truncate">{label}</span>
  </div>
);

const VerifyAccountModal = ({
  open,
  onClose,
  username,
  email,
  onEmailChange,
  emailAvailability,
  emailAvailabilityMessage,
  emailOtp,
  onEmailOtpChange,
  emailOtpSent,
  emailOtpVerified,
  emailOtpLoading,
  emailOtpError,
  emailOtpTimer,
  emailFieldError,
  onSendEmailOtp,
  onVerifyEmailOtp,
  onResendEmailOtp,
  mobile,
  onMobileChange,
  mobileAvailability,
  mobileAvailabilityMessage,
  mobileOtp,
  onMobileOtpChange,
  otpSent,
  otpVerified,
  otpLoading,
  otpError,
  otpTimer,
  mobileFieldError,
  onSendMobileOtp,
  onVerifyMobileOtp,
  onResendMobileOtp,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const lastEmailAttempt = useRef('');
  const lastMobileAttempt = useRef('');

  useEffect(() => {
    if (!open || emailOtpVerified || !emailOtpSent || emailOtpLoading) return undefined;
    const otp = (emailOtp || '').replace(/\D/g, '');
    if (otp.length !== OTP_LENGTH || lastEmailAttempt.current === otp) return undefined;
    lastEmailAttempt.current = otp;
    onVerifyEmailOtp?.(otp);
    return undefined;
  }, [open, emailOtp, emailOtpSent, emailOtpVerified, emailOtpLoading, onVerifyEmailOtp]);

  useEffect(() => {
    if (!open || !emailOtpVerified || otpVerified || !otpSent || otpLoading) return undefined;
    const otp = (mobileOtp || '').replace(/\D/g, '');
    if (otp.length !== OTP_LENGTH || lastMobileAttempt.current === otp) return undefined;
    lastMobileAttempt.current = otp;
    onVerifyMobileOtp?.(otp);
    return undefined;
  }, [open, mobileOtp, otpSent, otpVerified, otpLoading, emailOtpVerified, onVerifyMobileOtp]);

  if (!open) return null;

  const emailReady = emailAvailability === 'available';
  const mobileReady = mobileAvailability === 'available';
  const showMobile = emailOtpVerified;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-[2px]"
        aria-label="Close verify account"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-account-title"
        className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id="verify-account-title" className="text-xl font-bold text-gray-900 dark:text-white">
              Verify Account
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Confirm email, then mobile, for <span className="font-medium text-gray-700 dark:text-gray-200">{username}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <StepPill n={1} label="Email" active={!emailOtpVerified} done={emailOtpVerified} />
          <div className={`flex-1 h-px ${emailOtpVerified ? 'bg-emerald-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
          <StepPill n={2} label="Mobile" active={emailOtpVerified && !otpVerified} done={otpVerified} />
        </div>

        <div className="space-y-5">
          {emailOtpVerified ? (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2.5">
              <CheckBadge />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Email verified</p>
                <p className="text-xs text-emerald-700/80 dark:text-emerald-300 truncate">{email}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <label htmlFor="verify-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email address
              </label>
              <div className="flex gap-2">
                <input
                  id="verify-email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 min-w-0 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={onSendEmailOtp}
                  disabled={emailOtpLoading || !emailReady}
                  className="px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {emailOtpLoading && !emailOtpSent ? 'Sending…' : emailOtpSent ? 'Sent' : 'Send code'}
                </button>
              </div>
              {emailFieldError ? (
                <p className="text-sm text-red-500">{emailFieldError}</p>
              ) : emailAvailabilityMessage ? (
                <p className={`text-sm ${emailReady ? 'text-emerald-600 dark:text-emerald-400' : emailAvailability === 'checking' ? 'text-gray-500' : 'text-red-500'}`}>
                  {emailAvailability === 'checking' ? 'Checking email…' : emailAvailabilityMessage}
                </p>
              ) : null}

              {emailOtpSent && (
                <div className="space-y-3 pt-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Enter the 6-digit code</p>
                  <OtpDigitInputs
                    idPrefix="email-otp"
                    value={emailOtp}
                    onChange={onEmailOtpChange}
                    disabled={emailOtpLoading || emailOtpVerified}
                    error={Boolean(emailOtpError || emailFieldError)}
                  />
                  {emailOtpError ? <p className="text-sm text-red-500">{String(emailOtpError)}</p> : null}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={onResendEmailOtp}
                      disabled={emailOtpTimer > 0 || emailOtpLoading}
                      className="text-sm text-primary-600 dark:text-primary-400 disabled:text-gray-400"
                    >
                      {emailOtpTimer > 0 ? `Resend in ${emailOtpTimer}s` : 'Resend code'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onVerifyEmailOtp?.(emailOtp)}
                      disabled={emailOtpLoading || (emailOtp || '').replace(/\D/g, '').length !== OTP_LENGTH}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {emailOtpLoading ? 'Verifying…' : 'Verify email'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showMobile && (
            otpVerified ? (
              <div className="flex items-center gap-3 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2.5">
                <CheckBadge />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Mobile verified</p>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-300 truncate">{mobile}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-5">
                <label htmlFor="verify-mobile" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Mobile number
                </label>
                <div className="flex gap-2">
                  <input
                    id="verify-mobile"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => onMobileChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="9876543210"
                    className="flex-1 min-w-0 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={onSendMobileOtp}
                    disabled={otpLoading || !mobileReady}
                    className="px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {otpLoading && !otpSent ? 'Sending…' : otpSent ? 'Sent' : 'Send code'}
                  </button>
                </div>
                {mobileFieldError ? (
                  <p className="text-sm text-red-500">{mobileFieldError}</p>
                ) : mobileAvailabilityMessage ? (
                  <p className={`text-sm ${mobileReady ? 'text-emerald-600 dark:text-emerald-400' : mobileAvailability === 'checking' ? 'text-gray-500' : 'text-red-500'}`}>
                    {mobileAvailability === 'checking' ? 'Checking number…' : mobileAvailabilityMessage}
                  </p>
                ) : null}

                {otpSent && (
                  <div className="space-y-3 pt-1">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Enter the 6-digit code</p>
                    <OtpDigitInputs
                      idPrefix="mobile-otp"
                      value={mobileOtp}
                      onChange={onMobileOtpChange}
                      disabled={otpLoading || otpVerified}
                      error={Boolean(otpError || mobileFieldError)}
                    />
                    {otpError ? <p className="text-sm text-red-500">{String(otpError)}</p> : null}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={onResendMobileOtp}
                        disabled={otpTimer > 0 || otpLoading}
                        className="text-sm text-primary-600 dark:text-primary-400 disabled:text-gray-400"
                      >
                        {otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Resend code'}
                      </button>
                      <button
                        type="button"
                        onClick={() => onVerifyMobileOtp?.(mobileOtp)}
                        disabled={otpLoading || (mobileOtp || '').replace(/\D/g, '').length !== OTP_LENGTH}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      >
                        {otpLoading ? 'Verifying…' : 'Verify mobile'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          )}

          {otpVerified && (
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyAccountModal;
